package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.Div
import dev.danko.roblox_craft_ai.uikit.util.PageScrollLock
import org.w3c.dom.events.Event

/**
 * Side panel for an opened work (style A): slides over the grid from the right, closes on the backdrop,
 * the close button or Escape, and locks page scrolling while open.
 */
@Composable
fun AssetDrawer(
    title: String,
    onClose: () -> Unit,
    widthPx: Int = 640,
    /** Renames the work; with it the title gets an edit button. */
    onRename: (suspend (String) -> Unit)? = null,
    content: @Composable () -> Unit
) {
    val palette = LocalAppTheme.current.colors

    DisposableEffect(Unit) {
        PageScrollLock.acquire()
        val escape: (Event) -> Unit = { event -> if (event.asDynamic().key == "Escape") onClose() }
        document.addEventListener("keydown", escape)
        onDispose {
            PageScrollLock.release()
            document.removeEventListener("keydown", escape)
        }
    }

    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .backgroundColor(Color.rgba(0, 0, 0, 0.55f))
            .zIndex(60)
            .classNames("rc-backdrop-in")
            .onClick { onClose() }
            .toAttrs()
    )
    Aside(
        Modifier
            .position(Position.Fixed)
            .top(0.px).right(0.px)
            .height(100.vh)
            .width(widthPx.px)
            .maxWidth(100.vw)
            .backgroundColor(palette.surface)
            .styleModifier {
                property("border-left", "1px solid ${palette.borderStrong}")
                property("box-shadow", "-24px 0 48px rgba(0,0,0,0.35)")
            }
            .zIndex(61)
            .classNames("rc-slide-in")
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .flexDirection(org.jetbrains.compose.web.css.FlexDirection.Column)
            .toAttrs {
                attr("role", "dialog")
                attr("aria-modal", "true")
                attr("aria-label", title.take(80))
            }
    ) {
        Row(
            Modifier.fillMaxWidth().padding(topBottom = 14.px, leftRight = 20.px).gap(12.px)
                .styleModifier { property("border-bottom", "1px solid ${palette.border}") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (onRename != null) {
                EditableTitle(title, onRename, Modifier.flexGrow(1))
            } else SpanText(
                title,
                Modifier
                    .flexGrow(1)
                    .fontSize(18.px)
                    .fontWeight(FontWeight.Bold)
                    .color(palette.textPrimary)
                    .styleModifier {
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                    }
            )
            AppButton(
                text = "",
                icon = AppIcon.CLOSE,
                ariaLabel = strings().common.close,
                onClick = onClose,
                variant = AppButtonVariant.GHOST,
                size = AppButtonSize.MD,
                modifier = Modifier.width(44.px).padding(0.px)
            )
        }
        Box(Modifier.fillMaxWidth().flexGrow(1).overflow(Overflow.Auto).padding(16.px)) {
            content()
        }
    }
}
